/**
 * Formatting utilities for regulations and IBC fields
 */

// Helper function to format USCG Group presentation
export const formatUSCGGroup = (uscgGroup: any): string => {
    if (!uscgGroup) return '';
    return `${uscgGroup.number} ${uscgGroup.name}`;
};

// Helper function to format IMDG Class presentation
export const formatIMDGClass = (imdgClass: any): string => {
    if (!imdgClass) return '';
    return `${imdgClass.classification} ${imdgClass.description}`;
};

// Helper function to map allowance enum to string (FOSFA/EU)
// API uses 1-based values: 1=Not Listed, 2=Acceptable, 3=Banned
export const formatAllowance = (allowance: number | null | undefined): string => {
    if (allowance === null || allowance === undefined) return '';
    const allowanceMap: { [key: number]: string } = {
        1: 'Not Listed',
        2: 'Acceptable',
        3: 'Banned',
    };
    return allowanceMap[allowance] || '';
};

// Helper function to map NIOP allowance to string
// The API stores NIOP in two fields: niop1Allowance and niop2Allowance
// - niop1Allowance = 2 means "List 1 Acceptable"
// - niop2Allowance = 2 means "List 2 Acceptable"
// - Otherwise, niop1Allowance value is used directly (0=Not Specified, 1=Not Listed)
export const formatNIOPAllowance = (
    niop1Allowance: number | null | undefined,
    niop2Allowance?: number | null | undefined
): string => {
    // If niop1Allowance is 2, it means "List 1 Acceptable"
    if (niop1Allowance === 2) {
        return 'List 1 Acceptable';
    }
    // If niop2Allowance is 2, it means "List 2 Acceptable"
    if (niop2Allowance === 2) {
        return 'List 2 Acceptable';
    }
    // Otherwise use niop1Allowance value directly
    if (niop1Allowance === 1) {
        return 'Not Listed';
    }
    // 0 or null/undefined means not specified
    return '';
};

// Helper function to format boolean as Yes/No
export const formatYesNo = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value ? 'Yes' : 'No';
};

// Re-export formatDate from centralized dateFormatters for backward compatibility
export { formatDate } from './dateFormatters';

// Helper function to format Marpol regulation enum
export const formatMarpolRegulation = (reg: number | null | undefined): string => {
    if (reg === null || reg === undefined) return '';
    const regMap: { [key: number]: string } = {
        0: 'None',
        1: 'Annex I',
        2: 'Annex II - Chapter 17',
        3: 'Annex II - Chapter 18',
    };
    return regMap[reg] || '';
};

// Helper function to format Marpol pollution category
export const formatPollutionCategory = (category: number | null | undefined): string => {
    if (category === null || category === undefined) return '';
    const categoryMap: { [key: number]: string } = {
        0: 'None',
        1: 'X',
        2: 'Y',
        3: 'Z',
    };
    return categoryMap[category] || '';
};

// Helper function to format IBC Hazards Rules (d)
export const formatIBCHazards = (hazards: number | null | undefined): string => {
    if (hazards === null || hazards === undefined) return '';
    const hazardsMap: { [key: number]: string } = {
        1: 'S',
        2: 'P',
        3: 'S/P',
    };
    return hazardsMap[hazards] || '';
};

// Helper function to format IBC Ship Type (e)
export const formatShipType = (shipType: number | null | undefined): string => {
    if (shipType === null || shipType === undefined) return '';
    const shipTypeMap: { [key: number]: string } = {
        1: '1',
        2: '2',
        3: '2k',
        4: '3',
    };
    return shipTypeMap[shipType] || '';
};

// Helper function to format IBC Tank Type (f)
export const formatTankType = (tankType: number | null | undefined): string => {
    if (tankType === null || tankType === undefined) return '';
    const tankTypeMap: { [key: number]: string } = {
        1: '1G',
        2: '2G',
        3: '1P',
        4: '2P',
    };
    return tankTypeMap[tankType] || '';
};

// Helper function to format IBC Tank Ventilation (g)
export const formatTankVents = (vents: number | null | undefined): string => {
    if (vents === null || vents === undefined) return '';
    const ventsMap: { [key: number]: string } = {
        1: 'Controlled',
        2: 'Open',
    };
    return ventsMap[vents] || '';
};

// Helper function to format IBC Environment Control (h)
export const formatEnvironmentalControl = (control: number | null | undefined): string => {
    if (control === null || control === undefined) return '';
    const controlMap: { [key: number]: string } = {
        1: 'Inert',
        2: 'Pad',
        3: 'Dry',
        4: 'Ventilation',
        5: 'No',
        6: 'Pad+(vent or inert)',
        7: 'Pad+inert',
        8: 'Vent or pad (gas)',
    };
    return controlMap[control] || '';
};

// Helper function to format IBC Gauging (j)
export const formatGauging = (gauging: number | null | undefined): string => {
    if (gauging === null || gauging === undefined) return '';
    const gaugingMap: { [key: number]: string } = {
        1: 'Open',
        2: 'Restricted',
        3: 'Closed',
    };
    return gaugingMap[gauging] || '';
};

// Helper function to format IBC Vapour Detection (k)
export const formatVapourDetection = (detection: number | null | undefined): string => {
    if (detection === null || detection === undefined) return '';
    const detectionMap: { [key: number]: string } = {
        1: 'F',
        2: 'T',
        3: 'F/T',
        4: '-',
    };
    return detectionMap[detection] || '';
};

// Helper function to format IBC Fire Protection (l)
export const formatFireProtection = (protectionArray: number[] | null | undefined): string => {
    if (!protectionArray || protectionArray.length === 0) return '';
    const protectionMap: { [key: number]: string } = {
        1: 'A',
        2: 'B',
        3: 'C',
        4: 'D',
        5: 'No',
    };
    return protectionArray.map((p) => protectionMap[p] || p.toString()).join('');
};
